/**
 * Lesson Parser Utility
 * 
 * Parses custom .nles (Neura Lesson) files to extract metadata and content
 * File format:
 * ---
 * title: Lesson Title
 * duration: 45 mins
 * category: AI Basics
 * difficulty: Beginner
 * tags: machine learning, neural networks
 * ---
 * 
 * # Markdown Content Starts Here
 * 
 * Regular markdown content for the lesson...
 */

/**
 * Parse a .nles file content into metadata and content
 * @param {string} fileContent - The content of the .nles file
 * @returns {Object} - Object containing metadata and content
 */
export const parseNlesFile = (fileContent) => {
  try {
    // Split the file into metadata section and content section
    const parts = fileContent.split('---');
    
    if (parts.length < 3) {
      throw new Error('Invalid .nles file format. Must contain metadata section between --- markers.');
    }
    
    // Parse metadata (the content between the first and second ---)
    const metadataSection = parts[1].trim();
    const metadata = {};
    
    metadataSection.split('\n').forEach(line => {
      const [key, ...valueParts] = line.split(':');
      if (key && valueParts.length > 0) {
        const value = valueParts.join(':').trim();
        
        // Handle special fields
        if (key.trim() === 'tags') {
          metadata[key.trim()] = value.split(',').map(tag => tag.trim());
        } else {
          metadata[key.trim()] = value;
        }
      }
    });
    
    // Get the content (everything after the second ---)
    const content = parts.slice(2).join('---').trim();
    
    return {
      metadata,
      content
    };
  } catch (error) {
    console.error('Error parsing .nles file:', error);
    throw error;
  }
};

/**
 * Validate that a lesson has all required metadata
 * @param {Object} lesson - The parsed lesson object
 * @returns {boolean} - Whether the lesson is valid
 */
export const validateLesson = (lesson) => {
  const requiredFields = ['title', 'duration', 'category', 'difficulty'];
  
  for (const field of requiredFields) {
    if (!lesson.metadata[field]) {
      console.error(`Missing required field in lesson metadata: ${field}`);
      return false;
    }
  }
  
  if (!lesson.content || lesson.content.length < 10) {
    console.error('Lesson content is missing or too short');
    return false;
  }
  
  return true;
};

/**
 * Load a lesson from a file path
 * @param {string} filePath - Path to the .nles file
 * @returns {Promise<Object>} - Promise resolving to lesson object
 */
export const loadLessonFromFile = async (filePath) => {
  try {
    const response = await fetch(filePath);
    if (!response.ok) {
      throw new Error(`Failed to load lesson file: ${response.statusText}`);
    }
    
    const fileContent = await response.text();
    const lesson = parseNlesFile(fileContent);
    
    if (!validateLesson(lesson)) {
      throw new Error('Invalid lesson format');
    }
    
    return lesson;
  } catch (error) {
    console.error('Error loading lesson file:', error);
    throw error;
  }
};

/**
 * Generate a unique lesson ID
 * @param {string} courseId - The course ID
 * @param {string} fileName - The file name (without extension)
 * @returns {string} - A unique lesson ID
 */
export const generateLessonId = (courseId, fileName) => {
  // Simple hash function for generating deterministic IDs from filename
  const hash = fileName.split('').reduce((acc, char) => {
    return acc + char.charCodeAt(0);
  }, 0);
  
  return `${courseId}_${hash}`;
}; 